import { FieldConfig, OrderCustomFieldDifinitions, OrderCustomFields } from "@app/types/orders";
import { CustomField, CustomFieldType, FileFieldValue } from "@app/domain";

/** `FieldConfig['type']` (the real API's field-config union) uses different
 * names from v2's `CustomFieldType` for the same two concepts — everything
 * else is spelled identically. */
const FIELD_TYPE_MAP: Record<FieldConfig["type"], CustomFieldType> = {
  singleLine: "text",
  multiLine: "multilineText",
  phone: "phone",
  email: "email",
  number: "number",
  decimalNumber: "decimal",
  boolean: "boolean",
  date: "date",
  dateTime: "dateTime",
  select: "select",
  file: "file",
  json: "json",
};

/**
 * Resolves one entity's (order/customer/driver/pickup/dropoff) raw custom
 * field values + definitions into the `CustomField[]` shape the v2 UI
 * renders — the single implementation of the select-choice/file-unwrap/
 * boolean rules that used to be duplicated per-entity across
 * `order.slice.ts`/`customer.slice.ts` (and, for order-level fields, was
 * never unwrapped for `file` at all — a legacy inconsistency this does not
 * reproduce, since every entity should follow the real API contract the
 * same way).
 *
 * Called once per entity, with that entity's own values/definitions — never
 * a shared/reused local variable — so there's no risk of one entity's
 * fields bleeding into another's (unlike the legacy `order.slice.ts`
 * reducer, where a stray shared variable name meant `state.order
 * .customFields` ended up holding whichever of pickup/the last dropoff's
 * fields were processed last, not the order's own).
 */
export function resolveCustomFields(
  values: OrderCustomFields | null | undefined,
  definitions: OrderCustomFieldDifinitions[] | null | undefined
): CustomField[] {
  if (!definitions || definitions.length === 0 || !values) return [];

  const fields: CustomField[] = [];

  definitions.forEach((definition) => {
    const rawValue = values[definition.key];
    if (rawValue === undefined || rawValue === "" || rawValue === null) return;

    let value: CustomField["value"];

    if (definition.config.type === "select") {
      value = definition.config.choices.find((choice) => choice.key === rawValue)?.label ?? "-";
    } else if (definition.config.type === "file") {
      const file = rawValue as { url: string; name: string; mimetype: string };
      value = { url: file.url, name: file.name, mimeType: file.mimetype } satisfies FileFieldValue;
    } else {
      value = rawValue as CustomField["value"];
    }

    fields.push({
      key: definition.key,
      label: definition.label,
      type: FIELD_TYPE_MAP[definition.config.type],
      value,
      tooltip: definition.toolTip || undefined,
    });
  });

  return fields;
}
